import { useCallback, useEffect, useRef, useState } from "react";

const ANALYTICS_SESSION_STORAGE_TOKEN = "bookingAnalyticsSessionToken";

/**
 * Inactivity logout + tab blur privacy for finance analytics.
 */
export function useAnalyticsSessionGuard({ enabled, inactivityMs = 180000, onExpire }) {
  const [privacyBlur, setPrivacyBlur] = useState(false);
  const timerRef = useRef(null);

  const clearSession = useCallback(() => {
    try {
      sessionStorage.removeItem(ANALYTICS_SESSION_STORAGE_TOKEN);
    } catch {
      /* ignore */
    }
    if (typeof onExpire === "function") onExpire();
  }, [onExpire]);

  const resetTimer = useCallback(() => {
    if (!enabled) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      clearSession();
    }, inactivityMs);
  }, [enabled, inactivityMs, clearSession]);

  useEffect(() => {
    if (!enabled) return undefined;
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((ev) => window.addEventListener(ev, resetTimer, { passive: true }));
    resetTimer();
    return () => {
      events.forEach((ev) => window.removeEventListener(ev, resetTimer));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [enabled, resetTimer]);

  useEffect(() => {
    if (!enabled) return undefined;
    const onVis = () => setPrivacyBlur(document.visibilityState === "hidden");
    document.addEventListener("visibilitychange", onVis);
    onVis();
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [enabled]);

  return { privacyBlur, clearSession };
}
